import { Injectable } from '@angular/core';
import { AlertController, Platform } from 'ionic-angular';
import { HttpProvider } from '../http/http';
import { AuthMapper, AuthProvider } from '../authorization';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { SegmentLocationNames } from '../../model/types/analytics.const';
import { LoginProvider } from '../myria/login-provider';
import { ResourceProvider } from '../resource/resource';
import { LoggerEvent, LoggerProvider } from '../logger/logger';
import { APP_CONFIG } from '../../environment';

import { KeychainTouchId } from '@ionic-native/keychain-touch-id';
import { StorageProvider } from '../storage/storage';
import { ConfigProvider } from '../config/config';
import { TranslateService } from '@ngx-translate/core';
import { StorageType } from '../../model/types/storage-type';
import { BridgeNativeType } from '../../model/types/bridge-types';
import { BIOMETRIC_KEY_INVALIDATED } from '../myria/app-variables';

@Injectable()
export class NativeBiometricsProvider {
  touchId: boolean = false;
  faceId: boolean = false;
  simulate: boolean = true;
  initialized: boolean = false;
  onResume;
  // Error from the last scan() that resolved false.
  lastScanError: any = null;

  constructor(
    private platform: Platform,
    private loginProvider: LoginProvider,
    private httpProvider: HttpProvider,
    private resourceProvider: ResourceProvider,
    private alertCtrl: AlertController,
    private storageProvider: StorageProvider,
    private translate: TranslateService,

    private fingerprint: KeychainTouchId,
  ) {
  }

  isAvailable(): Promise<any> {
    if (this.platform.is('cordova')) {
      return new Promise((resolve) => {
        this.initialized = true;
        this.fingerprint.isAvailable().then(
          (available) => {
            this.faceId = false;
            this.touchId = false;

            if (available === 'face') this.faceId = true;
            else if (available) this.touchId = true;

            resolve(available);
          },
          (err) => {
            LoggerProvider.warning(this, LoggerEvent.Info, 'biometric function not available or disabled', err);
            resolve(null);
          }
        );
      });
    }
    else if (this.simulate)
      return new Promise((resolve) => {
        this.touchId = true;
        resolve(true);
      });
    return null;
  }

  scan(): Promise<any> {
    this.lastScanError = null;
    if (this.platform.is('cordova')) {
      return new Promise((resolve) => {
        try {
          this.tryVerify(resolve);
        }
        catch (ex) {
          RmtPopupComponent.showErrorWithLogTranslation('page.authorizeBiometricsPage.biometricsFailedMessage');
          resolve(false);
        }
      });
    }
    else if (this.simulate) {
      return new Promise(async(resolve) => {
        const alert = this.alertCtrl.create({
          title: 'Biometrics',
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                this.lastScanError = 'Cancelled';
                resolve(false);
              }
            },
            {
              text: 'OK',
              handler: () => {
                this.storageProvider.get('SimulatedBiometrics').then((data) => {
                  resolve(data);
                });
                return true;
              }
            }
          ]
        });
        alert.setMode('ios');
        alert.present();
      });
    }
    return null;
  }

  private tryVerify(resolve) {
    this.fingerprint.verify(ConfigProvider.settings.keychainKey,this.translate.instant('page.authorizeBiometricsPage.localizedReasonContent')).then(
      data => resolve(data),
      err => {
        if (err === "FINGERPRINT_DIALOG_FAILED") {
          LoggerProvider.info(this, 'Fingerprint dialog failed !');
          //Subscribe on resume i.e. foreground
          try {
            this.onResume = this.platform.resume.subscribe(() => {
              try {
                this.tryVerify(resolve)
                this.onResume.unsubscribe();
              }
              catch (ex) {
                LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to unsubscribe platform resume for fingerprint');
              }
            });
          }
          catch (ex) {
            LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to subscribe platform resume for fingerprint');
            resolve(false);
          }
        } else if (typeof err === 'string' && err.indexOf('KeyPermanentlyInvalidated') !== -1) {
          LoggerProvider.warning(this, LoggerEvent.KeyChain, 'Biometric key permanently invalidated');
          resolve(BIOMETRIC_KEY_INVALIDATED);
        } else {
          LoggerProvider.error(this, LoggerEvent.KeyChain, err);
          this.lastScanError = err;
          resolve(false);
        }
      }
    );
  }

  public save(fingerPrintToken, userAuthenticationRequired = true): Promise<any> {
    const biometricsStore = { fingerPrintToken: fingerPrintToken, version: 1 };
    if (this.platform.is('cordova')) {
      return this.fingerprint.save(ConfigProvider.settings.keychainKey, JSON.stringify(biometricsStore), userAuthenticationRequired);
    }
    else if (this.simulate) {
      return this.storageProvider.set('SimulatedBiometrics', JSON.stringify(biometricsStore));
    }
  }

  public remove(): Promise<any> {
    this.storageProvider.remove(StorageType.LastBiometricCustomerId);
    this.storageProvider.remove(StorageType.FingerprintDeviceToken);
    if (this.platform.is('cordova')) {
      return this.fingerprint.delete(ConfigProvider.settings.keychainKey);
    }
    else if (this.simulate) {
      return this.storageProvider.remove('SimulatedBiometrics');
    }
  }

  public async processNativeRequest(type, request) {
    if (type === BridgeNativeType.Biometrics.IsAvailable) {
      const result = await this.isAvailable();
      const deviceToken = await this.storageProvider.get(StorageType.FingerprintDeviceToken);
      const enabled = !!(deviceToken && deviceToken.length > 0);
      const biometricType = this.faceId ? 'faceId' : 'touchId';
      return {
        available: !!result,
        enabled,
        biometricType,
        result,
        touchId: this.touchId,
        faceId: this.faceId,
        simulate: this.simulate,
        initialized: this.initialized,
      };
    } else if (type === BridgeNativeType.Biometrics.Scan) {
      const result = await this.scan();
      // scan() can resolve false, null or BIOMETRIC_KEY_INVALIDATED, none of which are JSON.
      let stored = null;
      try {
        stored = typeof result === 'string' && result !== BIOMETRIC_KEY_INVALIDATED ? JSON.parse(result) : null;
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.KeyChain, 'Failed to parse biometrics Keychain payload');
      }
      return {
        token: stored ? stored.fingerPrintToken : null,
        stored,
      };
    } else if (type === BridgeNativeType.Biometrics.Save) {
      const result = await this.save(request.token, request.userAuthenticationRequired);
      return result
    } else if (type === BridgeNativeType.Biometrics.Remove) {
      const result = await this.remove();
      return result
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/native/native-biometrics.ts